import { useCallback, useEffect, useMemo, useState } from 'react'
import { Eye, Pencil } from 'lucide-react'
import { assistantService } from '../api/services/assistant.service'
import { caseService } from '../api/services/case.service'
import { AppShell } from '../components/layout/AppShell'
import { IconActionButton, TableIconActions } from '../components/ui/table-icon-actions'
import { ListPagination } from '../components/ui/list-pagination'
import { SelectPicker, type SelectOption } from '../components/ui/select-picker'
import { useAuth } from '../context/AuthContext'
import { canMutateCase } from '../lib/case-capabilities'
import { DEFAULT_PAGE_SIZE, resolveListPage, type PaginationMeta } from '../lib/api-list'
import { USER_ROLES } from '../types/auth'
import { CASE_STATUS_LABELS, type Case } from '../types/case'
import type { AssistantDoctorLink } from '../types/assistant'

export function ReviewInboxPage() {
  const { session, can } = useAuth()
  const role = session?.role ?? null
  const isAssistant = role === USER_ROLES.ASSISTANT
  const [cases, setCases] = useState<Case[]>([])
  const [hasLoaded, setHasLoaded] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState<number>(DEFAULT_PAGE_SIZE)
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [doctorId, setDoctorId] = useState('')
  const [linkedDoctors, setLinkedDoctors] = useState<AssistantDoctorLink[]>([])

  useEffect(() => {
    if (!isAssistant) {
      return
    }

    void assistantService.doctorPicker().then((response) => {
      setLinkedDoctors(response.data ?? [])
    })
  }, [isAssistant])

  const doctorOptions = useMemo<SelectOption[]>(() => {
    const options = linkedDoctors
      .map((link) => {
        const doctor = link.doctor
        if (!doctor) {
          return null
        }

        return {
          value: doctor.id,
          label: `${doctor.first_name} ${doctor.surname}`.trim() || doctor.email,
        }
      })
      .filter((item): item is SelectOption => item !== null)

    return [{ value: '', label: 'All linked doctors' }, ...options]
  }, [linkedDoctors])

  const loadInbox = useCallback(async () => {
    try {
      const response = await caseService.reviewInbox({
        page,
        per_page: perPage,
        doctor_id: isAssistant && doctorId ? doctorId : undefined,
      })
      const { items, meta: nextMeta } = resolveListPage(response, page, perPage)
      setCases(items)
      setMeta(nextMeta)
    } catch {
      setCases([])
      setMeta(null)
    } finally {
      setHasLoaded(true)
    }
  }, [page, perPage, doctorId, isAssistant])

  useEffect(() => {
    void loadInbox()
  }, [loadInbox])

  const procedureDoctorLabel = (caseItem: Case) => {
    const procedure = caseItem.procedure?.name ?? '—'
    const doctor = caseItem.belongs_to_doctor?.name ?? '—'
    return `${procedure} / ${doctor}`
  }

  const canEditRow = (caseItem: Case) => {
    if (!can('cases-update') || caseItem.deleted_at || caseItem.is_archived) {
      return false
    }

    if (!isAssistant) {
      return false
    }

    return canMutateCase(session?.role, caseItem, linkedDoctors, session?.user.id)
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-2 py-2">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold">Cases for Review</h1>
            <p className="mt-1 text-sm text-ink-600">
              {isAssistant
                ? 'Cases your linked doctors opened for assistant review. You can view and edit, but not delete.'
                : 'Cases assigned to you that are currently under review.'}
            </p>
          </div>
          {isAssistant ? (
            <div className="w-full max-w-xs">
              <SelectPicker
                options={doctorOptions}
                value={doctorId}
                onChange={(value) => {
                  setDoctorId(value)
                  setPage(1)
                }}
                placeholder="Filter by doctor"
              />
            </div>
          ) : null}
        </div>

        <section className="overflow-hidden rounded-lg border border-parch-100 bg-white shadow-card">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-parch-100 bg-parch-50 text-ink-600">
                <tr>
                  <th className="px-4 py-3 font-medium">Reference</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Procedure / doctor</th>
                  <th className="px-4 py-3 font-medium">Procedure date</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {hasLoaded && cases.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-sm text-ink-500">
                      No cases for your review.
                    </td>
                  </tr>
                ) : (
                  cases.map((caseItem) => (
                    <tr key={caseItem.id} className="border-b border-parch-50 last:border-0">
                      <td className="px-4 py-3 font-medium">{caseItem.reference}</td>
                      <td className="px-4 py-3">
                        {caseItem.status ? CASE_STATUS_LABELS[caseItem.status] : '—'}
                      </td>
                      <td className="px-4 py-3">{procedureDoctorLabel(caseItem)}</td>
                      <td className="px-4 py-3">{caseItem.procedure_date ?? '—'}</td>
                      <td className="px-4 py-3">
                        <TableIconActions>
                          <IconActionButton
                            icon={Eye}
                            label="View"
                            to={`/cases/${caseItem.id}`}
                          />
                          {canEditRow(caseItem) ? (
                            <IconActionButton
                              icon={Pencil}
                              label="Edit"
                              to={`/cases/${caseItem.id}/edit`}
                            />
                          ) : null}
                        </TableIconActions>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <ListPagination
          meta={meta}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={(next) => {
            setPerPage(next)
            setPage(1)
          }}
        />
      </div>
    </AppShell>
  )
}
